/** Resolve the five merchant-owned brand facts into the full shopper-email palette. */
'use strict';

const { getDefaultStyles } = require('./styles');

const HEX = /^#[0-9a-f]{6}$/i;
const MIN_WHITE_TEXT_CONTRAST = 4.5;
const HEADER_LOGO_HEIGHT = '40px';
const TYPEFACES = Object.freeze({
  sans: '\'Hanken Grotesk\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif',
  serif: 'Georgia, \'Times New Roman\', Times, serif'
});
const CORNERS = Object.freeze({ square: '0px', soft: '8px', round: '24px' });
const PAPERS = Object.freeze({
  warm: { background: '#EFEAE1', content: '#FCFAF6', text: '#2A2620', secondary: '#6B6256' },
  white: { background: '#F4F4F2', content: '#FFFFFF', text: '#20201E', secondary: '#666660' }
});
const DEFAULT_FACTS = Object.freeze({ brandColor: '#B68A3E', typeface: 'sans', corners: 'soft', paper: 'warm', logo: null });

function hexToRgb(hex) {
  if (!HEX.test(String(hex || ''))) return null;
  const n = parseInt(hex.slice(1), 16);
  return { r: n >> 16, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}
function relativeLuminance(rgb) {
  const channel = value => {
    const n = value / 255;
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}
function contrastRatio(a, b) {
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  if (!rgbA || !rgbB) return 0;
  const [high, low] = [relativeLuminance(rgbA), relativeLuminance(rgbB)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
}
function mix(a, b, amount) {
  const left = hexToRgb(a);
  const right = hexToRgb(b);
  if (!left || !right) return a;
  return rgbToHex({
    r: left.r * (1 - amount) + right.r * amount,
    g: left.g * (1 - amount) + right.g * amount,
    b: left.b * (1 - amount) + right.b * amount
  });
}

function deriveAccessibleBrandColor(brandColor) {
  const original = HEX.test(String(brandColor || '')) ? brandColor.toUpperCase() : '#B68A3E';
  const originalRatio = contrastRatio(original, '#FFFFFF');
  if (originalRatio >= MIN_WHITE_TEXT_CONTRAST) {
    return { original, final: original, originalRatio, finalRatio: originalRatio, darkened: false };
  }
  let low = 0;
  let high = 1;
  for (let i = 0; i < 18; i += 1) {
    const mid = (low + high) / 2;
    if (contrastRatio(mix(original, '#000000', mid), '#FFFFFF') >= MIN_WHITE_TEXT_CONTRAST) high = mid;
    else low = mid;
  }
  const final = mix(original, '#000000', high);
  return { original, final, originalRatio, finalRatio: contrastRatio(final, '#FFFFFF'), darkened: true };
}

function resolveEmailStyles(facts) {
  facts = facts || DEFAULT_FACTS;
  const accessibility = deriveAccessibleBrandColor(facts.brandColor);
  const paper = PAPERS[facts.paper] || PAPERS.warm;
  return {
    ...getDefaultStyles(),
    primaryColor: accessibility.final,
    buttonTextColor: '#FFFFFF',
    accentColor: accessibility.final,
    linkColor: accessibility.final,
    tintColor: mix(accessibility.original, paper.content, 0.9),
    textColor: paper.text,
    secondaryTextColor: paper.secondary,
    backgroundColor: paper.background,
    contentBackgroundColor: paper.content,
    fontFamily: TYPEFACES[facts.typeface] || TYPEFACES.sans,
    buttonBorderRadius: CORNERS[facts.corners] || CORNERS.soft,
    cardBorderRadius: CORNERS[facts.corners] || CORNERS.soft,
    buttonStyle: 'filled',
    ...(facts.logo?.url ? { logo: { url: facts.logo.url, height: HEADER_LOGO_HEIGHT, alignment: 'center' } } : {}),
    accessibility
  };
}

function validateEmailStyles(facts) {
  if (facts == null) return { valid: true, errors: [] };
  const errors = [];
  const allowed = new Set(['brandColor', 'typeface', 'corners', 'paper', 'logo']);
  for (const key of Object.keys(facts)) if (!allowed.has(key)) errors.push(`Unknown brand fact: ${key}`);
  if (!HEX.test(String(facts.brandColor || ''))) errors.push('brandColor must be hex format (#RRGGBB)');
  if (!Object.hasOwn(TYPEFACES, facts.typeface)) errors.push('typeface must be sans or serif');
  if (!Object.hasOwn(CORNERS, facts.corners)) errors.push('corners must be square, soft or round');
  if (!Object.hasOwn(PAPERS, facts.paper)) errors.push('paper must be warm or white');
  if (!Object.hasOwn(facts, 'logo')) errors.push('logo must be a hosted PNG fact or null for a wordmark');
  if (facts.logo != null) {
    if (typeof facts.logo !== 'object' || Array.isArray(facts.logo)) errors.push('logo must be an object or null');
    else {
      const logoAllowed = new Set(['url', 'mimeType', 'pixelWidth', 'pixelHeight']);
      for (const key of Object.keys(facts.logo)) if (!logoAllowed.has(key)) errors.push(`Unknown logo fact: ${key}`);
    }
    if (!/^https:\/\//i.test(String(facts.logo?.url || ''))) errors.push('logo.url must be an https URL');
    if (facts.logo?.mimeType !== 'image/png') errors.push('logo.mimeType must be image/png');
    if (facts.logo?.pixelHeight !== 80) errors.push('logo.pixelHeight must be 80');
    if (!Number.isInteger(facts.logo?.pixelWidth) || facts.logo.pixelWidth < 1 || facts.logo.pixelWidth > 1200) errors.push('logo.pixelWidth must be an integer from 1 to 1200');
  }
  return { valid: errors.length === 0, errors };
}

module.exports = {
  resolveEmailStyles,
  validateEmailStyles,
  deriveAccessibleBrandColor,
  contrastRatio,
  MIN_WHITE_TEXT_CONTRAST,
  HEADER_LOGO_HEIGHT,
  TYPEFACES,
  CORNERS,
  PAPERS
};
